import type { CheckoutProductType } from './product_type.js';

/** Quem paga a reserva de cada produto: `'every-traveler'`, cada viajante com o
 *  próprio rateio; `'requester'`, só o bloco dono do rateio (`allocationOwnerId`)
 *  leva `allocations`. É a regra que o kit declara no contrato do produto
 *  (`productContract`, intelligence-ui-kit, que não importa este pacote) e com
 *  que o card monta o envio; o BFF julga o envio do chat por esta (#6640). O
 *  genui-kit testa que as duas batem. */
const ALLOCATION_OWNER: {
  readonly [P in CheckoutProductType]: 'every-traveler' | 'requester';
} = {
  rental: 'requester',
  hotel: 'requester',
  flight: 'every-traveler',
  bus: 'every-traveler',
  mobility: 'every-traveler',
};

export function allocationOwnerFor(
  productType: CheckoutProductType,
): 'every-traveler' | 'requester' {
  return ALLOCATION_OWNER[productType];
}

/** Dono do rateio no modo `allocationOwner: 'requester'` (multi de hotel): o
 *  solicitante quando ele é hóspede; numa reserva só para terceiros (#5160), o
 *  hóspede que define a política. `undefined` = nenhum dos dois entre os
 *  hóspedes — não há bloco de onde tirar a alocação. Compartilhado pelo form,
 *  pelo submit e pelo BFF para que os três escolham o MESMO bloco. */
export function allocationOwnerId(
  travelerIds: readonly string[],
  requesterId: string | undefined,
  policyTravelerId: string | undefined,
): string | undefined {
  if (requesterId && travelerIds.includes(requesterId)) return requesterId;
  if (policyTravelerId && travelerIds.includes(policyTravelerId)) return policyTravelerId;
  return undefined;
}

/**
 * Os quartos de uma reserva de hotel, cada um com o titular na frente (#5522):
 * o dono do rateio (`allocationOwnerId`) vai para a frente do quarto em que já
 * está, e os hóspedes se agrupam de `guestsPerRoom` em `guestsPerRoom`, na ordem
 * do envio — a mesma ordem com que o modal-session agrupa os quartos. Cada
 * titular paga o seu quarto com o próprio rateio; os acompanhantes não têm
 * rateio, como no produto legado.
 *
 * Sem a ocupação (card antigo do histórico) ou com uma contagem que não fecha em
 * quartos, a reserva fica com um quarto só, pago pelo dono: a regra de antes.
 * Compartilhado pela tela, pelo envio, pelo BFF e pelo agente para que os quatro
 * escolham os MESMOS titulares.
 */
export function hotelRooms(
  travelerIds: readonly string[],
  ownerId: string | undefined,
  guestsPerRoom: number | undefined,
): string[][] {
  if (!ownerId) return [];
  if (!guestsPerRoom || travelerIds.length % guestsPerRoom !== 0) {
    return [[ownerId, ...travelerIds.filter((id) => id !== ownerId)]];
  }
  const ordered = [...travelerIds];
  const ownerIndex = ordered.indexOf(ownerId);
  const head = ownerIndex - (ownerIndex % guestsPerRoom);
  if (ownerIndex > head) {
    [ordered[head], ordered[ownerIndex]] = [ordered[ownerIndex] as string, ordered[head] as string];
  }
  const rooms: string[][] = [];
  for (let index = 0; index < ordered.length; index += guestsPerRoom) {
    rooms.push(ordered.slice(index, index + guestsPerRoom));
  }
  return rooms;
}

/** Hóspedes por quarto de uma busca de hotel: o MAIOR quarto, a mesma redução
 *  que vira o `personsInRoom` do fornecedor (um só para todos os quartos).
 *  `undefined` sem quarto nenhum. */
export function hotelGuestsPerRoom(
  rooms: readonly { guests: number }[] | undefined,
): number | undefined {
  if (!rooms || rooms.length === 0) return undefined;
  return Math.max(...rooms.map((room) => room.guests));
}
